// src/components/login/PhoneAuth.tsx
import React, { useState } from 'react';
import { ConfirmationResult } from 'firebase/auth';
import { sendVerificationCode, confirmVerificationCode } from '../../auth/phoneauth';
import { exchangeFirebaseTokenForAccessTokens } from '../../auth/exchange';
import { PhoneInput } from "../ui/PhoneInput";
import Layout from "../layout/Layout";
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert"

export default function PhoneAuth() {
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
    const [step, setStep] = useState<'enter-phone' | 'enter-code'>('enter-phone');
    const [attemptsLeft, setAttemptsLeft] = useState(3);
    const [sendCount, setSendCount] = useState(0);
    const [cooldownActive, setCooldownActive] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [alertType, setAlertType] = useState<"default" | "destructive">("default");
    
    const isValidUSPhoneNumber = (value: string) => {
        const digitsOnly = value.replace(/\D/g, ''); 
        return digitsOnly.length === 11;
    };

    const handleSendCode = async () => {
        setAlertMessage(null);
        if (!isValidUSPhoneNumber(phone)) {
            showAlert('Please enter a valid 10-digit US phone number.', "destructive");
            return;
        }
        
        if (cooldownActive) {
            showAlert('Please wait before sending more codes.',"default");
            return;
        }
    
        if (sendCount >= 3) {
            setCooldownActive(true);
            setTimeout(() => {
                setSendCount(0);
                setCooldownActive(false);
            }, 60000); 
            showAlert('You’ve reached the limit of 3 code requests. Please wait 60 seconds before trying again.', "default");
            return;
        }
    
        try {
            console.log('Sending SMS to:', phone);
            const result = await sendVerificationCode(phone);
            setConfirmationResult(result);
            setStep('enter-code');
            setSendCount(prev => prev + 1);
        } catch (err) {
            console.error('Error sending SMS:', err);
            showAlert('Failed to send verification code. Please try again.', "destructive");
        }
    };

    const handleConfirmCode = async () => {
        setAlertMessage(null);
        if (!confirmationResult) return;
        try {
            const idToken = await confirmVerificationCode(confirmationResult, code);
            console.log('Code was correct, proceeding to backend exchange');
    
            try {
                await exchangeFirebaseTokenForAccessTokens(idToken);
                showAlert('Phone authentication successful and communication with backend successful!', "default");
            } catch (backendErr) {
                console.error('Backend token exchange failed:', backendErr);
                showAlert('Phone authentication successful, but communication with backend failed.', "default");

            }
    
        } catch (err) {
            console.error('Error confirming code:', err);
            const remaining = attemptsLeft - 1;
    
            if (remaining <= 0) {
                showAlert('Too many failed attempts. Please restart the login process. Redirecting...', "default");
                setTimeout(() => {
                    setStep('enter-phone');
                    setConfirmationResult(null);
                    setCode('');
                    setPhone('');
                    setAttemptsLeft(3);
                    setSendCount(0);
                    setCooldownActive(false);
                    setAlertMessage(null); 
                }, 3000);
            } else {
                showAlert(`Incorrect code. You have ${remaining} ${remaining === 1 ? 'attempt' : 'attempts'} left.`, "destructive");
                setAttemptsLeft(remaining);
            }
        }
    };

    const showAlert = (message: string, type: "default" | "destructive" = "default") => {
        setAlertMessage(message);
        setAlertType(type);
    };

    return (
        <div>
            <Layout
                colorMode = "gray"
                navbarConfig={{
                    title: "Guest View",
                    showLogo: true,
                    hamburgerMenu: false,
                }}
            >
                <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-4 -mt-20">
                    <div id='recaptcha-container'></div> 
                    {alertMessage && (
                        <Alert variant={alertType} className="max-w-md w-full">
                            <AlertTitle>{alertType === "destructive" ? "Error" : "Notice"}</AlertTitle>
                            <AlertDescription>{alertMessage}</AlertDescription>
                        </Alert>
                    )}
                    {step === 'enter-phone' && (
                        <>
                            Enter phone number (US) below: 
                            <PhoneInput
                                value={phone}
                                onChange={(val) => setPhone(val)}
                                defaultCountry="US" 
                                className="w-full max-w-md"
                            />
                            <Button onClick={handleSendCode} className="bg-[#2F1C0A] text-[#FBEFDA] rounded-full text-base font-semibold leading-[1.4] tracking-normal px-6 py-3 text-center">Send Code</Button>
                        </>
                    )}
                    {step === 'enter-code' && (
                        <>
                            Please enter your verificiation code:
                            <Input
                                type="text"
                                value={code}
                                placeholder="Enter verification code"
                                onChange={(e) => setCode(e.target.value)}
                            />
                            <Button onClick={handleConfirmCode} className="bg-[#2F1C0A] text-[#FBEFDA] rounded-full text-base font-semibold leading-[1.4] tracking-normal px-6 py-3 text-center">Verify Code</Button>
                            <Button onClick={handleSendCode} variant="outline" className=" rounded-full text-base font-semibold leading-[1.4] tracking-normal px-6 py-3 text-center">Didn't Receive a Code?</Button>
                        </>
                    )}
                </div>
            </Layout>
        </div>
    );
}
